import type { IAdapter, InitArguments } from '../event-client';
import type { EventTypes, EventPayloads, LoginPayload } from '../events';

const subscribedEvents: Array<EventTypes> = [
	'product-view',
	'page-view',
	'add-to-cart',
	'purchase',
	'proceed-to-checkout',
];

interface MailchimpEvent {
	store_id: number;
	event_name: string;
	properties: Record<string, any>;
	customer?: {
		email: string;
		opt_in_status?: boolean;
	};
	campaign_id?: string | undefined; // Only for purchase events
}

class Transformer {
	static getCampaignId(): string | null {
		return sessionStorage.getItem('mailchimp_campaign_id');
	}

	static transformPageView(
		data: EventPayloads['page-view'],
		storeId: number,
		email: string | null,
	): MailchimpEvent | undefined {
		if (!email) return undefined;
		return {
			store_id: storeId,
			event_name: 'page-view',
			properties: {
				url: data.location,
				title: document.title,
			},
			customer: { email },
		};
	}

	static transformProductView(
		data: EventPayloads['product-view'],
		storeId: number,
		email: string | null,
	): MailchimpEvent | undefined {
		if (!email) return undefined;
		const product = data.meta_data.product;
		return {
			store_id: storeId,
			event_name: 'product-view',
			properties: {
				product_id: product.shopify_id?.toString() || '',
				external_product_id: product.external_product_id?.toString() || '',
				product_name: product.title,
				price: product.price.toString(),
				currency: 'PKR',
			},
			customer: { email },
		};
	}

	static transformAddToCart(
		data: EventPayloads['add-to-cart'],
		storeId: number,
		email: string | null,
	): MailchimpEvent | undefined {
		if (!email) return undefined;
		const product = data.meta_data.product;
		return {
			store_id: storeId,
			event_name: 'add-to-cart',
			properties: {
				product_id: product.shopify_id?.toString() || '',
				external_product_id: product.external_product_id?.toString() || '',
				product_name: product.title,
				price: product.price.toString(),
				currency: 'PKR',
			},
			customer: { email },
		};
	}

	static transformProceedToCheckout(
		data: EventPayloads['proceed-to-checkout'],
		storeId: number,
		email: string | null,
	): MailchimpEvent | undefined {
		if (!email) return undefined;
		const itemsTransformed = data.meta_data.items.map((item: any) => ({
			product_id: item.productId?.split('/').at(-1) || '',
			variant_id: item.id?.split('/').at(-1) || '',
			product_name: item.productTitle,
			price: item.price.toString(),
			quantity: item.quantity.toString(),
		}));

		return {
			store_id: storeId,
			event_name: 'checkout-start',
			properties: {
				total: data.subTotal.toString() || '0',
				currency: data.currencyCode.toUpperCase() || 'PKR',
				items: JSON.stringify(itemsTransformed) || '[]',
			},
			customer: { email },
		};
	}

	static transformPurchase(
		data: EventPayloads['purchase'],
		storeId: number,
		email: string | null,
	): any | undefined {
		email = data.email || email;
		if (!email) return undefined;

		const campaignId = Transformer.getCampaignId();
		const purchaseEvent: any = {
			id: data.orderNumber || '',
			store_id: storeId,
			customer: {
				email: email,
				opt_in_status: false,
			},
			currency_code: data.currency.toUpperCase() || 'PKR',
			order_total: data.totalPrice || 0,
			shipping_total: data.shipping || 0,
			tax_total: data.totalTax || 0,
			lines:
				data.lineItems?.map((item: any) => ({
					id: (item.lineId && item.lineId.split('/').at(-1)) || '',
					product_id:
						(item.productId && item.productId.split('/').at(-1)) || '',
					product_variant_id: (item.id && item.id.split('/').at(-1)) || '',
					quantity: Number(item.quantity),
					price: item.price.toString(),
				})) || [],
		};

		if (campaignId) {
			purchaseEvent.campaign_id = campaignId;
		}

		return purchaseEvent;
	}
}

const TRANSFORMER_MAP: Partial<
	Record<
		EventTypes,
		(
			data: any,
			storeId: number,
			email: string | null,
		) => MailchimpEvent | undefined
	>
> = {
	'page-view': Transformer.transformPageView,
	'product-view': Transformer.transformProductView,
	'add-to-cart': Transformer.transformAddToCart,
	purchase: Transformer.transformPurchase,
	'proceed-to-checkout': Transformer.transformProceedToCheckout,
};

export class MailchimpAdapter implements IAdapter {
	name = 'mailchimp';
	private baseUrl: string = '';
	private storeId: number | null = null;
	private email: string | null = null;

	async init(arg: InitArguments['mailchimp']): Promise<void> {
		this.baseUrl = arg.baseUrl;
		this.storeId = arg.storeId;
		this.email = arg.email || null;

		// Capture campaign ID on initialization and store it
		const urlParams = new URLSearchParams(window.location.search);
		const campaignId = urlParams.get('mc_cid');
		if (campaignId) {
			sessionStorage.setItem('mailchimp_campaign_id', campaignId);
		}
	}

	async sendEvent<T extends EventTypes>(
		eventName: T,
		eventData: EventPayloads[T],
	): Promise<void> {
		if (!subscribedEvents.includes(eventName) || !this.storeId) {
			return;
		}

		const transformedEvent = TRANSFORMER_MAP[eventName]?.(
			eventData,
			this.storeId,
			this.email,
		);

		if (!transformedEvent) {
			return;
		}

		try {
			const logan = localStorage.getItem('logan')
				? JSON.parse(localStorage.getItem('logan')!)
				: '';
			const endpoint =
				eventName === 'purchase' ? 'mailchimp/purchases' : 'mailchimp/events';
			await fetch(`${this.baseUrl}${endpoint}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'logan-token': `${logan.accessToken}`,
				},
				body: JSON.stringify(transformedEvent),
			});
		} catch (error) {
			console.error('MailchimpAdapter: Error tracking event:', error);
		}
	}

	login(data: LoginPayload): void {
		this.email = data.email || null;
	}

	logout(): void {
		if (this.email) {
			this.email = null;
		}
	}
}
